import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Skeleton, Snackbar, Stack, ThemeProvider, Typography, useMediaQuery, useTheme } from '@mui/material';
import BalanceOutlined from '@mui/icons-material/BalanceOutlined';
import Header from '../../SharedComponents/Header/Header';
import User from '../../Models/UserModels/User';
import { NoteDetail, ReweightingReferences, StatusFilter, Worklist } from '../../Models/ReWeightingModels/Workspace';
import { fetchReweightingNote, fetchReweightingNotes, fetchReweightingReferences, isCancelled, reweightingError } from '../../Services/ReweightingWorkspaceService';
import NoteWorklist from './Components/Workspace/NoteWorklist';
import NoteEditor from './Components/Workspace/NoteEditor';
import { createWorkspaceTheme } from './workspaceTheme';

interface Props { user: User; darkMode: boolean; setDarkMode: (value: boolean) => void }
export default function ReWeighting({ user, darkMode, setDarkMode }: Props) {
  const theme = useTheme();
  const workspaceTheme = useMemo(() => createWorkspaceTheme(theme), [theme]);
  const compact = useMediaQuery(theme.breakpoints.down('md'));
  const [showDetail, setShowDetail] = useState(false);
  const [status, setStatus] = useState<StatusFilter>('open');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [list, setList] = useState<Worklist>();
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<NoteDetail>();
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [detailRefresh, setDetailRefresh] = useState(0);
  const [references, setReferences] = useState<ReweightingReferences>();
  const [referenceError, setReferenceError] = useState('');
  const [referenceRefresh, setReferenceRefresh] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [discardAction, setDiscardAction] = useState<(() => void) | null>(null);
  const [message, setMessage] = useState('');
  const listFilter = useRef('');

  useEffect(() => {
    const timeout = window.setTimeout(() => { setDebouncedSearch(search.trim()); setPage(1); }, 300);
    return () => window.clearTimeout(timeout);
  }, [search]);
  useEffect(() => {
    const abort = new AbortController();
    setLoading(true); setListError('');
    const filterKey = `${status}:${debouncedSearch}`;
    if (listFilter.current !== filterKey) { setList(undefined); listFilter.current = filterKey; }
    fetchReweightingNotes(status, debouncedSearch, page, abort.signal).then(result => {
      if (abort.signal.aborted) return;
      setList(result);
      const lastPage = Math.max(1, Math.ceil(result.total / result.pageSize));
      if (page > lastPage) setPage(lastPage);
    }).catch(error => { if (!isCancelled(error) && !abort.signal.aborted) setListError(reweightingError(error)); })
      .finally(() => { if (!abort.signal.aborted) setLoading(false); });
    return () => abort.abort();
  }, [status, debouncedSearch, page, refresh]);
  useEffect(() => {
    // Bakgrunnsuppfærsla má ekki loka óvistuðum innslætti eða rjúfa vistun.
    if (!list || list.page !== page || dirty || busy) return;
    setSelectedId(current => list.items.some(note => note.id === current) ? current : list.items[0]?.id ?? null);
  }, [list, page, dirty, busy]);
  useEffect(() => {
    if (selectedId === null) {
      setDetail(undefined); setDetailError(''); setDetailLoading(false); setDirty(false); setShowDetail(false);
      return;
    }
    const abort = new AbortController();
    setDetailLoading(true); setDetailError('');
    setDetail(previous => previous?.note.id === selectedId ? previous : undefined);
    setDirty(false);
    fetchReweightingNote(selectedId, abort.signal).then(result => { if (!abort.signal.aborted) setDetail(result); })
      .catch(error => { if (!isCancelled(error) && !abort.signal.aborted) setDetailError(reweightingError(error)); })
      .finally(() => { if (!abort.signal.aborted) setDetailLoading(false); });
    return () => abort.abort();
  }, [selectedId, detailRefresh]);
  useEffect(() => {
    const abort = new AbortController();
    setReferenceError('');
    fetchReweightingReferences(abort.signal).then(result => { if (!abort.signal.aborted) setReferences(result); })
      .catch(error => { if (!isCancelled(error) && !abort.signal.aborted) setReferenceError(reweightingError(error)); });
    return () => abort.abort();
  }, [referenceRefresh]);
  useEffect(() => {
    if (!dirty && !busy) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, busy]);

  const guard = useCallback((action: () => void) => {
    if (busy) return;
    if (dirty) setDiscardAction(() => action); else action();
  }, [busy, dirty]);
  const select = (id: number) => {
    if (id === selectedId) { setShowDetail(true); return; }
    guard(() => { setSelectedId(id); setShowDetail(true); });
  };
  const saved = (value: NoteDetail) => {
    setDetail(value); setDirty(false); setRefresh(current => current + 1);
  };
  const submitted = (value: string) => {
    setDirty(false); setMessage(value); setRefresh(current => current + 1); setDetailRefresh(current => current + 1);
  };
  return (
    <ThemeProvider theme={workspaceTheme}>
      <Box sx={{ width: '100%', minWidth: 0, height: '100dvh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default', color: 'text.primary',
        '& *': { scrollbarWidth: 'thin', scrollbarColor: `${workspaceTheme.palette.text.secondary} transparent` },
        '@media (prefers-reduced-motion: reduce)': { '& *, & *::before, & *::after': { animation: 'none !important', transition: 'none !important' } } }}>
        <Header user={user} isReWeighing selectedPage="Endurvigtun" isAuthorized darkMode={darkMode} setDarkMode={setDarkMode} />
        {referenceError && <Alert severity="warning" action={<Button color="inherit" size="small" onClick={() => setReferenceRefresh(value => value + 1)}>Reyna aftur</Button>}>Ekki tókst að sækja vallista. {referenceError}</Alert>}
        <Box component="main" sx={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '352px minmax(0, 1fr)', xl: '376px minmax(0, 1fr)' } }}>
          <Box sx={{ minWidth: 0, minHeight: 0, borderRightStyle: 'solid', borderRightWidth: { xs: 0, md: 1 }, borderColor: 'divider', bgcolor: 'background.default', display: compact && showDetail ? 'none' : 'block' }}>
            <NoteWorklist data={list} loading={loading} error={listError} selectedId={selectedId} status={status} search={search} page={page}
              disabled={busy} onStatus={value => guard(() => { setStatus(value); setPage(1); })}
              onSearch={value => guard(() => setSearch(value))} onPage={value => guard(() => setPage(value))}
              onSelect={select} onRefresh={() => setRefresh(value => value + 1)} />
          </Box>
          <Box sx={{ minWidth: 0, minHeight: 0, bgcolor: 'background.paper', display: compact && !showDetail ? 'none' : 'block' }} aria-busy={detailLoading}>
            {detailLoading && !detail ? <Box sx={{ height: '100%', overflow: 'auto', display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', xl: 'minmax(0, 1fr) 264px' },
              gridTemplateAreas: { xs: '"summary" "context" "weights"', xl: '"summary context" "weights context"' }, gridTemplateRows: { xs: 'auto auto 1fr', xl: 'auto 1fr' } }}>
              <Box sx={{ gridArea: 'summary', px: { xs: 2, sm: 3 }, pt: 2, pb: 2.5, borderBottom: 1, borderColor: 'divider' }}><Skeleton width={150} height={32} sx={{ mb: 1.5 }} /><Skeleton width="65%" height={42} /><Skeleton width="55%" height={26} /></Box>
              <Box sx={{ gridArea: 'context', p: 3, bgcolor: 'background.default', display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', xl: '1fr' }, alignContent: 'start', gap: 2 }}>
                {Array.from({ length: 3 }, (_, index) => <Box key={index}><Skeleton width="60%" /><Skeleton width="90%" height={28} /></Box>)}
              </Box>
              <Box sx={{ gridArea: 'weights', minWidth: 0, p: { xs: 2, sm: 3 } }}>
                <Box sx={{ mb: 3, pb: 2.5, borderBottom: 1, borderColor: 'divider' }}>
                  <Skeleton width={160} height={28} />
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1} sx={{ my: 1 }}>
                    <Skeleton width="55%" height={28} /><Skeleton variant="rounded" width={150} height={34} />
                  </Stack>
                  <Skeleton width={200} height={28} />
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1} sx={{ mb: 2 }}>
                  <Box><Skeleton height={30} width={220} /><Skeleton width={180} /></Box>
                  <Skeleton variant="rounded" width={170} height={34} />
                </Stack>
                <Skeleton variant="rounded" height={40} sx={{ mb: 1.5 }} />
                {Array.from({ length: 4 }, (_, index) => <Skeleton key={index} variant="rectangular" height={62} sx={{ mt: 1, borderRadius: 1 }} />)}
              </Box>
            </Box> : detailError ? <Stack spacing={2} sx={{ p: 4 }}>
              <Alert severity="error">{detailError}</Alert><Button onClick={() => setDetailRefresh(value => value + 1)}>Reyna aftur</Button>
              {compact && <Button onClick={() => setShowDetail(false)}>Aftur í nótulista</Button>}
            </Stack> : detail ? (
              <NoteEditor key={`${detail.note.id}:${detailRefresh}`} detail={detail} references={references} compact={compact} refreshing={detailLoading}
                onDirty={setDirty} onBusy={setBusy} onSaved={saved} onSubmitted={submitted}
                onReload={() => guard(() => setDetailRefresh(value => value + 1))} onBack={() => setShowDetail(false)} />
            ) : <Stack justifyContent="center" alignItems="center" spacing={2} sx={{ height: '100%', p: 4, textAlign: 'center' }}>
              <Box sx={{ p: 2, borderRadius: 3, bgcolor: 'action.selected', color: 'primary.main', display: 'flex' }}><BalanceOutlined sx={{ fontSize: 32 }} /></Box>
              <Typography fontSize={20} fontWeight={650}>Veldu vigtarnótu</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300 }}>Nótur og staða skila birtast í listanum. Veldu nótu til að yfirfara eða skrá afla.</Typography>
            </Stack>}
          </Box>
        </Box>
        <Dialog open={!!discardAction} onClose={() => setDiscardAction(null)} maxWidth="xs" fullWidth aria-labelledby="discard-changes-title">
          <DialogTitle id="discard-changes-title">Fara án þess að vista?</DialogTitle>
          <DialogContent><Typography>Óvistaðar breytingar á þessari nótu glatast.</Typography></DialogContent>
          <DialogActions><Button onClick={() => setDiscardAction(null)}>Halda áfram að vinna</Button>
            <Button color="error" onClick={() => {
              const action = discardAction;
              setDiscardAction(null); setDirty(false); setDetailRefresh(value => value + 1); action?.();
            }}>Henda breytingum</Button></DialogActions>
        </Dialog>
        <Snackbar open={!!message} autoHideDuration={7000} onClose={() => setMessage('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity="success" variant="filled" onClose={() => setMessage('')} sx={{ width: '100%' }}>{message}</Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}
